import { useContext, useState, useEffect } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { Table, Card, Select, Button, Statistic, Row, Col, Space, Typography, Spin, Alert } from "antd";
import { ReloadOutlined, DollarOutlined, MinusCircleOutlined, CheckCircleOutlined } from "@ant-design/icons";
import axios from "axios";

const { Title } = Typography;
const { Option } = Select;

export default function GrossSalary() {
    const { authData, backUrl } = useContext(AuthContext);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await axios.get(`${backUrl}/api/payroll/salaries/`, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`
                }
            });
            setData(res.data.results);
        } catch (err) {
            setError(err.message || "Failed to fetch data");
            console.error("Error fetching payroll data:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [selectedMonth, selectedYear]);

    const getMonthYearDisplay = (month, year) => {
        const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        return `${monthNames[month - 1]} ${year}`;
    };

    // Calculate totals for summary cards
    const calculateTotals = () => {
        if (!data?.results) return { grossTotal: 0, basicTotal: 0, hraTotal: 0, conveyanceTotal: 0, otherTotal: 0 };

        return data.results.reduce((acc, record) => ({
            grossTotal: acc.grossTotal + parseFloat(record.gross_earned || 0),
            basicTotal: acc.basicTotal + parseFloat(record.basic_earned || 0),
            hraTotal: acc.hraTotal + parseFloat(record.hra_earned || 0),
            conveyanceTotal: acc.conveyanceTotal + parseFloat(record.conveyance_earned || 0),
            otherTotal: acc.otherTotal + parseFloat(record.other_allowance_earned || 0)
        }), { grossTotal: 0, basicTotal: 0, hraTotal: 0, conveyanceTotal: 0, otherTotal: 0 });
    };

    const totals = data ? calculateTotals() : null;

    const columns = [
        {
            title: "S.No",
            key: "sno",
            width: 70,
            fixed: "left",
            align: "center",
            render: (text, record, index) => index + 1
        },
        {
            title: "Month–Year",
            key: "monthYear",
            width: 130,
            render: (text, record) => getMonthYearDisplay(record.month, record.year)
        },
        {
            title: "Employee ID",
            key: "employee_code",
            width: 120,
            render: (text, record) => record.employee?.emp_code || "N/A"
        },
        {
            title: "Employee Name",
            key: "employee_name",
            width: 180,
            render: (text, record) => {
                const firstName = record.employee?.first_name || "";
                const lastName = record.employee?.last_name || "";
                return `${firstName} ${lastName}`.trim() || "N/A";
            }
        },
        {
            title: "Designation",
            key: "designation",
            width: 150,
            render: (text, record) => record.employee?.designation_name || "N/A"
        },
        {
            title: "Department",
            key: "department",
            width: 150,
            render: (text, record) => record.employee?.department_name || "N/A"
        },
        {
            title: "Site/Location",
            key: "location",
            width: 150,
            render: (text, record) => record.employee?.location || "N/A"
        },
        {
            title: "Gross Salary",
            dataIndex: "gross_pm",
            key: "gross_pm",
            width: 130,
            align: "right",
            render: (value) => `₹${parseFloat(value || 0).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`
        },
        {
            title: "Basic",
            dataIndex: "basic_earned",
            key: "basic_earned",
            width: 130,
            align: "right",
            render: (value) => `₹${parseFloat(value || 0).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`
        },
        {
            title: "HRA",
            dataIndex: "hra_earned",
            key: "hra_earned",
            width: 130,
            align: "right",
            render: (value) => `₹${parseFloat(value || 0).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`
        },
        {
            title: "Conveyance",
            dataIndex: "conveyance_earned",
            key: "conveyance_earned",
            width: 130,
            align: "right",
            render: (value) => `₹${parseFloat(value || 0).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`
        },
        {
            title: "Special Allowance",
            dataIndex: "flexible_benefit_allowance",
            key: "flexible_benefit_allowance",
            width: 150,
            align: "right",
            render: (value) => `₹${parseFloat(value || 0).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`
        },
        {
            title: "Other Allowance",
            dataIndex: "other_allowance_earned",
            key: "other_allowance_earned",
            width: 140,
            align: "right",
            render: (value) => `₹${parseFloat(value || 0).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`
        },
        {
            title: "Total Gross Payable",
            dataIndex: "gross_earned",
            key: "gross_earned",
            width: 160,
            align: "right",
            render: (value) => (
                <span style={{ color: "#52c41a", fontWeight: "600" }}>
                    ₹{parseFloat(value || 0).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    })}
                </span>
            )
        },
        {
            title: "Remarks",
            dataIndex: "remarks",
            key: "remarks",
            width: 200,
            render: (value) => value || "-"
        }
    ];

    const monthOptions = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const yearOptions = Array.from(
        { length: 5 },
        (_, i) => new Date().getFullYear() - i
    );

    return (
        <>
            <Table
                columns={columns}
                dataSource={data.results}
                rowKey="id"
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} records`,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    total: data.count
                }}
                scroll={{ x: 2000 }}
                bordered
                size="middle"
                locale={{
                    emptyText: `No payroll data found for ${getMonthYearDisplay(selectedMonth, selectedYear)}`
                }}
                components={{
                    header: {
                        cell: (props) => (
                            <th {...props} style={{ backgroundColor: "#0e497b", color: 'white', marginTop: '20px' }}>
                                {props.children}
                            </th>
                        ),
                    },
                }}
            />
        </>
    );
}